import { apiClient } from './client';

export interface BillingOrder {
    id: string;
    orderNumber: number;
    userId?: string;
    userEmail?: string;
    userName?: string;
    date: string;
    amount: number;
    currency: string;
    status: 'PAID' | 'PENDING' | 'FAILED' | 'REFUNDED';
    type: 'INITIAL' | 'RECURRING' | 'ONE_OFF';
    invoicePdf?: string;
    hostedInvoiceUrl?: string;
    planName?: string;
    paymentMethod?: string;
    metadata?: string;
}

export interface Coupon {
    id: string;
    code: string;
    name?: string;
    amountOff?: number;
    percentOff?: number;
    currency?: string;
    duration: 'once' | 'repeating' | 'forever';
    durationInMonths?: number;
    maxRedemptions?: number | null;
    timesRedeemed: number;
    isActive: boolean;

    // Restrictions
    startsAt?: string | null;
    endsAt?: string | null;
    appliesToPlans?: string[];
    minAmount?: number;
    maxRedemptionsPerUser?: number | null;
}

export const billingApi = {
    getAllOrders: async (): Promise<BillingOrder[]> => {
        const response = await apiClient.get('/billing/orders');
        return response.data.data;
    },

    getCustomerOrders: async (customerId: string): Promise<BillingOrder[]> => {
        const response = await apiClient.get('/billing/orders', { params: { userId: customerId } });
        return response.data.data;
    },

    getOrder: async (id: string): Promise<BillingOrder> => {
        const response = await apiClient.get(`/billing/orders/${id}`);
        return response.data.data;
    },

    refundOrder: async (orderId: string, amount?: number, reason?: string): Promise<BillingOrder> => {
        const response = await apiClient.post(`/billing/orders/${orderId}/refund`, { amount, reason });
        return response.data.data;
    },

    getCoupons: async (): Promise<Coupon[]> => {
        const response = await apiClient.get('/billing/coupons');
        return response.data.data;
    },

    createCoupon: async (data: Partial<Coupon>): Promise<Coupon> => {
        const response = await apiClient.post('/billing/coupons', data);
        return response.data.data;
    },

    getCoupon: async (id: string): Promise<Coupon> => {
        const response = await apiClient.get(`/billing/coupons/${id}`);
        return response.data.data;
    },

    updateCoupon: async (id: string, data: Partial<Coupon>): Promise<Coupon> => {
        const response = await apiClient.put(`/billing/coupons/${id}`, data);
        return response.data.data;
    },

    deleteCoupon: async (id: string): Promise<void> => {
        await apiClient.delete(`/billing/coupons/${id}`);
    },

    getUserTimeline: async (userId: string): Promise<TimelineEvent[]> => {
        const response = await apiClient.get(`/billing/timeline/user/${userId}`);
        return response.data.data;
    },

    getOrderTimeline: async (orderId: string): Promise<TimelineEvent[]> => {
        const response = await apiClient.get<any>(`/billing/timeline/order/${orderId}`);
        return response.data.data;
    },

    getCustomerDetails: async (customerId: string): Promise<any> => {
        const response = await apiClient.get(`/billing/customers/${customerId}/details`);
        return response.data.data;
    },

    getCustomerBalance: async (customerId: string): Promise<{ balance: number; currency: string }> => {
        const response = await apiClient.get(`/billing/customers/${customerId}/balance`);
        return response.data.data;
    },

    updateCustomerAddress: async (customerId: string, address: any): Promise<any> => {
        const response = await apiClient.put(`/billing/customers/${customerId}/address`, address);
        return response.data.data;
    },

    cancelCustomerSubscription: async (customerId: string, immediately: boolean): Promise<any> => {
        const response = await apiClient.post(`/billing/customers/${customerId}/subscription/cancel`, { immediately });
        return response.data.data;
    },

    resumeCustomerSubscription: async (customerId: string): Promise<any> => {
        const response = await apiClient.post(`/billing/customers/${customerId}/subscription/resume`);
        return response.data.data;
    },

    changeCustomerPlan: async (customerId: string, priceId: string, immediate?: boolean): Promise<any> => {
        const response = await apiClient.put(`/billing/customers/${customerId}/subscription`, { priceId, immediate });
        return response.data.data;
    }

};

export interface TimelineEvent {
    id: string;
    category: string;
    type: string;
    title: string;
    description: string;
    metadata: string;
    createdAt: string;
    billingOrderId?: string | null;
}
